import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from '../../utils/setupAxios';

const formatDateTime = (ts) => {
  if (!ts) return '-';
  const d = new Date(ts);
  return isNaN(d.getTime()) ? String(ts) : d.toLocaleString();
};

const CARD_FIELDS = ['uid', 'name', 'tid', 'country', 'status'];

function CardsTable({ cards }) {
  const [selectedUid, setSelectedUid] = useState(null);
  const [cardSnap, setCardSnap] = useState(null);
  const [snapLoading, setSnapLoading] = useState(false);
  const [snapError, setSnapError] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 15;

  const knownFields = cards.length > 0
    ? CARD_FIELDS.filter((f) => cards.some((c) => c[f] != null))
    : [];
  const hasKnownFields = knownFields.length > 0;

  const paged = cards.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const lastPage = Math.max(1, Math.ceil(cards.length / PAGE_SIZE));

  const fetchSnap = useCallback(async () => {
    setSnapLoading(false);
    setSnapError(null);
    setCardSnap(null);
  }, []);

  const handleUidClick = (uid) => {
    if (selectedUid === uid) {
      setSelectedUid(null);
      setCardSnap(null);
    } else {
      setSelectedUid(uid);
      fetchSnap(uid);
    }
  };

  if (cards.length === 0) {
    return <p className="text-muted px-3">No cards in collection.</p>;
  }

  return (
    <>
      {lastPage > 1 && (
        <div className="d-flex align-items-center px-3 py-2">
          <span className="text-muted small">{cards.length} cards</span>
          <ul className="pagination pagination-sm m-0 ms-auto">
            <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => setPage((p) => Math.max(1, p - 1))}>‹</button>
            </li>
            {Array.from({ length: lastPage }, (_, i) => i + 1)
              .filter((n) => Math.abs(n - page) <= 2)
              .map((n) => (
                <li key={n} className={`page-item ${n === page ? 'active' : ''}`}>
                  <button className="page-link" onClick={() => setPage(n)}>{n}</button>
                </li>
              ))}
            <li className={`page-item ${page >= lastPage ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => setPage((p) => Math.min(lastPage, p + 1))}>›</button>
            </li>
          </ul>
        </div>
      )}
      <div className="table-responsive">
        <table className="table table-vcenter card-table table-sm">
          <thead>
            <tr>
              {hasKnownFields ? (
                <>
                  {knownFields.map((f) => <th key={f}>{f}</th>)}
                  <th>Actions</th>
                </>
              ) : (
                <th>Data</th>
              )}
            </tr>
          </thead>
          <tbody>
            {paged.map((card, idx) => (
              <React.Fragment key={card.uid ?? idx}>
                <tr>
                  {hasKnownFields ? (
                    <>
                      {knownFields.map((f) => (
                        <td key={f}>
                          {f === 'uid' && card.uid ? (
                            <code
                              className="text-primary"
                              style={{ cursor: 'pointer' }}
                              onClick={() => handleUidClick(card.uid)}
                              title="Click to load card snap"
                            >
                              {card.uid}
                            </code>
                          ) : (
                            card[f] != null ? String(card[f]) : '-'
                          )}
                        </td>
                      ))}
                      <td>
                        {card.uid && (
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => handleUidClick(card.uid)}
                          >
                            {selectedUid === card.uid ? 'Hide snap' : 'View snap'}
                          </button>
                        )}
                      </td>
                    </>
                  ) : (
                    <td><pre className="mb-0 small">{JSON.stringify(card, null, 2)}</pre></td>
                  )}
                </tr>
                {selectedUid === card.uid && (
                  <tr>
                    <td colSpan={knownFields.length + 1} className="bg-light">
                      {snapLoading && (
                        <div className="py-3 text-center">
                          <div className="spinner-border spinner-border-sm text-muted" role="status" />
                        </div>
                      )}
                      {snapError && <div className="text-danger small p-2">{snapError}</div>}
                      {cardSnap && (
                        <div className="p-3">
                          <div className="row g-2 mb-2">
                            <div className="col-auto">
                              <span className="text-muted small">UID:</span>{' '}
                              <code className="small">{cardSnap.uid}</code>
                            </div>
                            <div className="col-auto">
                              <span className="text-muted small">Name:</span>{' '}
                              <strong className="small">{cardSnap.name ?? '-'}</strong>
                            </div>
                            <div className="col-auto">
                              <span className="text-muted small">Created:</span>{' '}
                              <span className="small">{formatDateTime(cardSnap.created_at)}</span>
                            </div>
                            <div className="col-auto">
                              <span className="text-muted small">Updated:</span>{' '}
                              <span className="small">{formatDateTime(cardSnap.updated_at)}</span>
                            </div>
                          </div>
                          {cardSnap.content && (
                            <div className="mb-2">
                              <div className="text-muted small mb-1">Content (recognition result):</div>
                              <pre className="small bg-white border rounded p-2 mb-0" style={{ maxHeight: 200, overflow: 'auto' }}>
                                {JSON.stringify(cardSnap.content, null, 2)}
                              </pre>
                            </div>
                          )}
                          {cardSnap.static_url && (
                            <div>
                              <div className="text-muted small mb-1">Static URLs:</div>
                              <pre className="small bg-white border rounded p-2 mb-0" style={{ maxHeight: 100, overflow: 'auto' }}>
                                {JSON.stringify(cardSnap.static_url, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default function UserDetail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('user_id');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteMsg, setDeleteMsg] = useState(null);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get('/holo/users/detail', { params: { user_id: userId } });
      const data = res?.data?.data ?? null;
      if (!data) {
        setError(res?.data?.message || 'User not found');
      } else {
        setDetail(data);
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load user detail');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) fetchDetail();
  }, [userId, fetchDetail]);

  const handleDeleteAll = async () => {
    if (!window.confirm(`Delete all cards for user ${userId}? This cannot be undone.`)) return;
    setDeleting(true);
    setDeleteMsg(null);
    try {
      const res = await axiosInstance.post('/holo/users/delete-all', { user_id: userId });
      const msg = res?.data?.message || 'All cards deleted.';
      setDeleteMsg({ type: 'success', text: msg });
      fetchDetail();
    } catch (e) {
      setDeleteMsg({ type: 'danger', text: e?.response?.data?.message || e?.message || 'Delete failed' });
    } finally {
      setDeleting(false);
    }
  };

  if (!userId) {
    return (
      <div className="container-xl">
        <div className="alert alert-warning mt-4">
          Missing user_id.{' '}
          <button className="btn btn-link p-0 align-baseline" onClick={() => navigate('/users')}>
            Back to Users
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container-xl">
      <div className="page-header d-print-none mb-3">
        <div className="row align-items-center">
          <div className="col">
            <button className="btn btn-link p-0 text-muted mb-1" onClick={() => navigate('/users')}>
              ← Users
            </button>
            <h2 className="page-title mb-0">
              User ID: <code>{userId}</code>
            </h2>
          </div>
          <div className="col-auto d-flex gap-2">
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={fetchDetail}
              disabled={loading}
            >
              {loading ? <span className="spinner-border spinner-border-sm" role="status" /> : 'Reload'}
            </button>
            <button
              className="btn btn-danger btn-sm"
              onClick={handleDeleteAll}
              disabled={deleting || loading}
            >
              {deleting ? <span className="spinner-border spinner-border-sm" role="status" /> : 'Delete all cards'}
            </button>
          </div>
        </div>
      </div>

      {deleteMsg && (
        <div className={`alert alert-${deleteMsg.type} alert-dismissible`} role="alert">
          {deleteMsg.text}
          <button type="button" className="btn-close" onClick={() => setDeleteMsg(null)} />
        </div>
      )}

      {loading && !detail && (
        <div className="text-center py-5">
          <div className="spinner-border text-muted" role="status" />
        </div>
      )}

      {error && !detail && (
        <div className="alert alert-danger">{error}</div>
      )}

      {detail && (
        <>
          {/* Info card */}
          <div className="card mb-3">
            <div className="card-header">
              <h3 className="card-title">User Info</h3>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-auto">
                  <div className="text-muted small">Cards count</div>
                  <div className="fs-3 fw-bold">{detail.coins_count ?? 0}</div>
                </div>
                <div className="col-auto">
                  <div className="text-muted small">Created</div>
                  <div>{formatDateTime(detail.created_at)}</div>
                </div>
                <div className="col-auto">
                  <div className="text-muted small">Updated</div>
                  <div>{formatDateTime(detail.updated_at)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Cards collection */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Card Collection</h3>
              <div className="card-options text-muted small">
                {Array.isArray(detail.coins) ? `${detail.coins.length} items` : ''}
              </div>
            </div>
            <CardsTable cards={Array.isArray(detail.coins) ? detail.coins : []} />
          </div>
        </>
      )}
    </div>
  );
}
